/**
 * Reusable category picker with inline "+ Add new category".
 *
 * Renders a native <select> populated with MERGED options:
 *   - Built-in choices (hardcoded per form, type-safe enum values)
 *   - Custom categories the studio has added (live from Firestore
 *     via `useCustomCategories(orgId, scope)`)
 *   - A final "+ Add new category…" option that opens an inline
 *     modal asking for a label. On save, writes a new
 *     `customCategories` doc and selects it in place.
 *
 * Usage:
 *   <CategorySelectWithAdd
 *     orgId={orgId}
 *     scope="payment_in"
 *     builtIn={TRANSACTION_CATEGORY_OPTIONS_BY_TYPE['payment_in']}
 *     value={category}
 *     onChange={setCategory}
 *     allowEmpty
 *   />
 *
 * The `value` and `onChange` are plain strings — built-in enum
 * values stay typed; custom-category ids are strings too. Caller
 * persists whatever string is selected. (The form schema should
 * accept `string` rather than the strict enum union so custom
 * ids round-trip cleanly.)
 */
import { useMemo, useState } from 'react';

import { useAuth } from '@/auth/AuthProvider';
import {
  createCustomCategory,
  useCustomCategories,
  type CustomCategoryScope,
} from '@/lib/customCategories';

export type BuiltInOption = { value: string; label: string };

export type CategorySelectWithAddProps = {
  orgId: string | null;
  scope: CustomCategoryScope;
  /** Built-in categories shown above the custom + add row. */
  builtIn: readonly BuiltInOption[];
  /** Currently selected value. Empty string when none. */
  value: string;
  /** Called with the new value (built-in enum OR custom category id). */
  onChange: (next: string) => void;
  /** Show "No category" option at the top. Default true. */
  allowEmpty?: boolean;
  /** Label shown above the select. */
  label?: string;
  /** Disable + dim. */
  disabled?: boolean;
  /** Inline style overrides for the select. */
  selectStyle?: React.CSSProperties;
};

export function CategorySelectWithAdd({
  orgId,
  scope,
  builtIn,
  value,
  onChange,
  allowEmpty = true,
  label,
  disabled,
  selectStyle,
}: CategorySelectWithAddProps) {
  const { user } = useAuth();
  const { data: customs } = useCustomCategories(orgId, scope);
  const [adding, setAdding] = useState(false);

  // Merge built-in + custom, then append the special "Add new…" row.
  const options = useMemo(() => {
    const out: { value: string; label: string; isAdd?: boolean }[] = [];
    if (allowEmpty) out.push({ value: '', label: 'No category' });
    for (const b of builtIn) out.push({ value: b.value, label: b.label });
    if (customs.length > 0) {
      // Visual separator — a disabled divider option
      out.push({ value: '__divider__', label: '──────────' });
      for (const c of customs) {
        // Store the LITERAL label as the value. Persisted on the
        // doc as `category: 'Diwali Bonus'` (vs built-ins which
        // are enum keys like 'rent'). Display layer handles both:
        // look up built-in by key, else show the string as-is.
        // Trade-off: renaming a custom category later won't update
        // historic rows — same model as Notion tags. Accepted.
        out.push({ value: c.label, label: c.label });
      }
    }
    // The "Add new" sentinel — handled in onChange.
    out.push({ value: '__add__', label: '+ Add new category…', isAdd: true });
    return out;
  }, [builtIn, customs, allowEmpty]);

  const handleChange = (next: string) => {
    if (next === '__divider__') return; // ignored
    if (next === '__add__') {
      setAdding(true);
      return;
    }
    onChange(next);
  };

  return (
    <>
      {label ? (
        <label
          style={{
            display: 'block',
            fontSize: 12,
            color: 'var(--text-secondary)',
            marginBottom: 4,
            fontWeight: 500,
          }}
        >
          {label}
        </label>
      ) : null}
      <select
        className="input"
        value={value}
        onChange={(e) => handleChange(e.currentTarget.value)}
        disabled={disabled}
        style={selectStyle}
      >
        {options.map((o) =>
          o.value === '__divider__' ? (
            <option key="__divider__" disabled>
              {o.label}
            </option>
          ) : (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ),
        )}
      </select>

      {adding ? (
        <AddCategoryModal
          scope={scope}
          orgId={orgId}
          createdBy={user?.uid ?? ''}
          existingSortOrders={customs.map((c) => c.sortOrder)}
          onSaved={(id) => {
            onChange(id);
            setAdding(false);
          }}
          onClose={() => setAdding(false)}
        />
      ) : null}
    </>
  );
}

// ───── Add modal ────────────────────────────────────────────

function AddCategoryModal({
  scope,
  orgId,
  createdBy,
  existingSortOrders,
  onSaved,
  onClose,
}: {
  scope: CustomCategoryScope;
  orgId: string | null;
  createdBy: string;
  existingSortOrders: number[];
  onSaved: (id: string) => void;
  onClose: () => void;
}) {
  const [labelText, setLabelText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (!labelText.trim()) {
      setError('Category label is required.');
      return;
    }
    if (!orgId || !createdBy) {
      setError('Not ready — please reload.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // Sort order = max existing + 10 so the new one lands at the
      // bottom and there's room to manually reorder later.
      const max = existingSortOrders.length
        ? Math.max(...existingSortOrders)
        : 0;
      await createCustomCategory({
        orgId,
        scope,
        label: labelText,
        sortOrder: max + 10,
        createdBy,
      });
      // Pass the LABEL back (not the doc id) — that's what the
      // picker uses as `value` for custom rows, and what the form
      // persists on the doc.
      onSaved(labelText.trim());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save.');
      setBusy(false);
    }
  }

  return (
    <>
      <div
        className="slide-overlay open"
        onClick={() => !busy && onClose()}
        style={{ background: 'rgba(15,23,42,0.45)' }}
      />
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 220,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
          pointerEvents: 'none',
        }}
      >
        <div
          className="card"
          style={{
            width: '100%',
            maxWidth: 380,
            background: 'var(--surface)',
            borderRadius: 12,
            boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
            pointerEvents: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{
              padding: '14px 18px',
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <h3 style={{ margin: 0, fontSize: 16 }}>Add category</h3>
            <button
              type="button"
              onClick={() => !busy && onClose()}
              aria-label="Close"
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: 18,
                cursor: 'pointer',
                color: 'var(--text-secondary)',
              }}
            >
              <i className="ph ph-x" />
            </button>
          </div>

          <div style={{ padding: 18 }}>
            <label
              style={{
                display: 'block',
                fontSize: 12,
                color: 'var(--text-secondary)',
                marginBottom: 4,
                fontWeight: 500,
              }}
            >
              Category name *
            </label>
            <input
              className="input"
              type="text"
              value={labelText}
              onChange={(e) => setLabelText(e.currentTarget.value)}
              placeholder="e.g. Diwali Bonus, Marketing Photoshoot"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') void save();
              }}
            />
            <div
              style={{
                marginTop: 6,
                fontSize: 11,
                color: 'var(--text-tertiary)',
              }}
            >
              This category will be available in this dropdown for your whole
              studio. You can edit or archive it later from settings.
            </div>
            {error ? (
              <div
                style={{
                  marginTop: 10,
                  padding: 10,
                  borderRadius: 6,
                  background: '#FEE2E2',
                  color: '#B91C1C',
                  fontSize: 12,
                }}
              >
                {error}
              </div>
            ) : null}
          </div>

          <div
            style={{
              padding: '12px 18px',
              borderTop: '1px solid var(--border)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
            }}
          >
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={save}
              disabled={busy}
            >
              {busy ? 'Saving…' : 'Add'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
